/**
 * ChapterEditor Page Component
 * WYSIWYG editor for creating and editing chapters
 */
import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { Chapter } from "@novelreader/types";
import {
  api,
  type CreateChapterRequest,
  type UpdateChapterRequest,
  type Tier,
} from "../lib/api";
import { TipTapEditor } from "../components/TipTapEditor";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ChapterEditorProps {
  mode: "create" | "edit";
  novelId?: string;
  chapterId?: string;
  initialData?: Chapter;
  onSubmit?: (data: ChapterFormData) => Promise<void>;
}

export interface ChapterFormData {
  title: string;
  slug: string;
  content: string;
  tierId: string | null; // NULL = free access
  isPublished: boolean;
}

// ---------------------------------------------------------------------------
// Utility Functions
// ---------------------------------------------------------------------------

function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ChapterEditor({
  mode,
  novelId: propNovelId,
  chapterId: propChapterId,
  initialData: propInitialData,
  onSubmit: propOnSubmit,
}: ChapterEditorProps) {
  const navigate = useNavigate();
  const params = useParams<{ id: string; chapterId: string }>();

  // Use props or URL params
  const novelId = propNovelId || params.id;
  const chapterId = propChapterId || params.chapterId;

  const isEditMode = mode === "edit";

  // Form state
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState("");
  const [content, setContent] = useState("");
  const [tierId, setTierId] = useState<string | null>(null);

  // Tiers state
  const [tiers, setTiers] = useState<Tier[]>([]);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(isEditMode);
  const [errors, setErrors] = useState<{ title?: string; content?: string }>(
    {},
  );
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  // Track initial published status for display
  const [wasPublished, setWasPublished] = useState(false);

  // Novel title for breadcrumb
  const [novelTitle, setNovelTitle] = useState<string>("");

  // Fetch novel title for breadcrumb
  useEffect(() => {
    if (!novelId) return;
    api.novels.get(novelId).then((res) => setNovelTitle(res.novel.title));
  }, [novelId]);

  // Fetch tiers for the author
  useEffect(() => {
    api.tiers
      .list()
      .then((res) => setTiers(res.tiers))
      .catch(() => {
        // On error, keep empty tiers array - free option is always available
      });
  }, []);

  // Initialize with propInitialData if provided
  useEffect(() => {
    if (propInitialData) {
      setTitle(propInitialData.title);
      setSlug(propInitialData.slug);
      setContent(propInitialData.content);
      setTierId(propInitialData.tierId);
      setWasPublished(Boolean(propInitialData.publishedAt));
      setIsFetching(false);
    }
  }, [propInitialData]);

  // Fetch chapter data in edit mode
  const fetchChapter = useCallback(async () => {
    if (!isEditMode || !novelId || !chapterId || propInitialData) return;

    setIsFetching(true);
    try {
      const response = await api.chapters.get(novelId, chapterId);
      const chapter = response.chapter;
      setTitle(chapter.title);
      setSlug(chapter.slug);
      setContent(chapter.content);
      setTierId(chapter.tierId);
      setWasPublished(Boolean(chapter.publishedAt));
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to load chapter",
      );
    } finally {
      setIsFetching(false);
    }
  }, [isEditMode, novelId, chapterId, propInitialData]);

  useEffect(() => {
    fetchChapter();
  }, [fetchChapter]);

  // Auto-generate slug from title
  useEffect(() => {
    if (!isEditMode && !slugManuallyEdited) {
      setSlug(generateSlug(title));
    }
  }, [title, isEditMode, slugManuallyEdited]);

  // Clear errors
  useEffect(() => {
    if (title && errors.title) {
      setErrors((prev) => ({ ...prev, title: undefined }));
    }
  }, [title, errors.title]);

  useEffect(() => {
    if (content && errors.content) {
      setErrors((prev) => ({ ...prev, content: undefined }));
    }
  }, [content, errors.content]);

  async function handleSubmit(e: React.FormEvent, shouldPublish: boolean) {
    e.preventDefault();

    // Validate
    const newErrors: typeof errors = {};
    if (!title.trim()) {
      newErrors.title = "Title is required";
    }
    if (!content.trim()) {
      newErrors.content = "Content is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // If custom onSubmit provided, use it
    if (propOnSubmit) {
      await propOnSubmit({
        title: title.trim(),
        slug: slug.trim() || generateSlug(title),
        content: content.trim(),
        tierId,
        isPublished: shouldPublish,
      });
      return;
    }

    if (!novelId) return;

    setIsLoading(true);
    setSubmitError(null);

    try {
      if (isEditMode && chapterId) {
        // Update chapter
        const data: UpdateChapterRequest = {
          title: title.trim(),
          content: content.trim(),
          tierId,
          publishedAt: shouldPublish ? new Date().toISOString() : null,
        };
        await api.chapters.update(novelId, chapterId, data);
      } else {
        // Create chapter
        const data: CreateChapterRequest = {
          title: title.trim(),
          content: content.trim(),
          tierId,
          publishedAt: shouldPublish ? new Date().toISOString() : null,
        };
        await api.chapters.create(novelId, data);
      }

      navigate(`/novels/${novelId}/chapters`);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save chapter",
      );
    } finally {
      setIsLoading(false);
    }
  }

  function handleCancel() {
    if (novelId) {
      navigate(`/novels/${novelId}/chapters`);
    } else {
      navigate("/novels");
    }
  }

  // Loading state
  if (isFetching) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-slate-200 rounded animate-pulse" />
        <div className="flex gap-6">
          <div className="flex-1 space-y-4">
            <div className="h-10 bg-slate-200 rounded animate-pulse" />
            <div className="h-10 bg-slate-200 rounded animate-pulse" />
            <div className="h-96 bg-slate-200 rounded animate-pulse" />
          </div>
          <div className="flex-1 h-96 bg-slate-200 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  const pageTitle = isEditMode ? "Edit Chapter" : "Create Chapter";

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-slate-500">
        <Link to="/novels" className="hover:text-slate-700">
          Novels
        </Link>
        <span>/</span>
        <Link
          to={`/novels/${novelId}/chapters`}
          className="hover:text-slate-700"
        >
          {novelTitle || "..."}
        </Link>
        <span>/</span>
        <span className="text-slate-900 font-medium">
          {isEditMode ? "Edit Chapter" : "New Chapter"}
        </span>
      </nav>

      {/* Header */}
      <h1 className="text-2xl font-bold text-slate-900">{pageTitle}</h1>

      {/* Error Alert */}
      {submitError && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {submitError}
        </div>
      )}

      {/* Editor Container */}
      <div className="max-w-4xl">
        <div data-testid="editor-section" role="region" aria-label="Editor">
          <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
            {/* Title Field */}
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Title
              </label>
              <input
                id="title"
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className={`w-full px-3 py-2 border rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${
                  errors.title ? "border-red-500" : "border-slate-300"
                }`}
                placeholder="Enter chapter title"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-600">{errors.title}</p>
              )}
            </div>

            {/* Slug Field */}
            <div>
              <label
                htmlFor="slug"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Slug
              </label>
              <input
                id="slug"
                type="text"
                value={slug}
                onChange={(e) => {
                  setSlug(e.target.value);
                  if (!isEditMode) setSlugManuallyEdited(true);
                }}
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                placeholder="url-friendly-slug"
              />
            </div>

            {/* Content Field */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Content
              </label>
              <TipTapEditor
                content={content}
                onChange={setContent}
                placeholder="Write your chapter content here..."
              />
              {errors.content && (
                <p className="mt-1 text-sm text-red-600">{errors.content}</p>
              )}
            </div>

            {/* Tier Selection */}
            <div>
              <label
                htmlFor="tier"
                className="block text-sm font-medium text-slate-700 mb-1"
              >
                Access Tier
              </label>
              <select
                id="tier"
                value={tierId ?? ""}
                onChange={(e) => setTierId(e.target.value || null)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="">Free (No tier required)</option>
                {tiers.map((tier) => (
                  <option key={tier.id} value={tier.id}>
                    {tier.name} (Level {tier.level})
                  </option>
                ))}
              </select>
            </div>

            {/* Bottom Bar */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-200">
              <div className="flex items-center gap-4">
                {isEditMode && (
                  <span
                    className={`text-xs px-2 py-1 rounded-full ${
                      wasPublished
                        ? "bg-green-100 text-green-700"
                        : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {wasPublished ? "Published" : "Draft"}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, false)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {isLoading ? "Saving..." : "Save as Draft"}
                </button>
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
                >
                  {isLoading ? "Publishing..." : "Publish"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ChapterEditor;
